import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

type ConsentState = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
};

export const COOKIE_STORAGE_KEY = '4kdream_cookie_consent';
const STORAGE_KEY = COOKIE_STORAGE_KEY;

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState<ConsentState>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      // Small delay so the page loads first, then banner slides in
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const save = (consent: ConsentState) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...consent, timestamp: new Date().toISOString() })
    );
    setVisible(false);
  };

  const acceptAll = () =>
    save({ essential: true, analytics: true, marketing: true });

  const acceptEssential = () =>
    save({ essential: true, analytics: false, marketing: false });

  const saveCustom = () => save(prefs);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none">
      <div
        className="max-w-4xl mx-auto bg-gray-900 border border-white/15 rounded-2xl pointer-events-auto"
        style={{ boxShadow: '0 -4px 40px rgba(0,0,0,0.6)' }}
      >
        {/* Main bar */}
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#862ce0]/20 border border-[#862ce0]/30 flex-shrink-0">
              <i className="ri-shield-check-line text-[#b47ef3] text-lg"></i>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-base sm:text-lg leading-tight mb-1">
                We value your privacy
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                We use cookies to enhance your browsing experience, serve personalised ads, and analyse our traffic. By clicking{' '}
                <strong className="text-white">"Accept All"</strong>, you consent to our use of cookies.{' '}
                <Link
                  to="/privacy"
                  className="text-[#b47ef3] hover:text-white underline underline-offset-2 transition-colors whitespace-nowrap"
                >
                  Privacy Policy
                </Link>
              </p>
            </div>
          </div>

          {/* Expandable preferences */}
          {expanded && (
            <div className="mb-5 bg-gray-800/60 border border-white/10 rounded-xl p-4 space-y-3">
              {/* Essential */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-white text-sm font-semibold">Essential Cookies</p>
                  <p className="text-gray-500 text-xs mt-0.5">Required for the site to function. Cannot be disabled.</p>
                </div>
                <div className="w-11 h-6 rounded-full bg-[#862ce0] flex items-center justify-end px-1 flex-shrink-0 cursor-not-allowed opacity-70">
                  <div className="w-4 h-4 rounded-full bg-white"></div>
                </div>
              </div>

              <div className="border-t border-white/10"></div>

              {/* Analytics */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-white text-sm font-semibold">Analytics Cookies</p>
                  <p className="text-gray-500 text-xs mt-0.5">Help us understand how visitors interact with our site.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setPrefs((p) => ({ ...p, analytics: !p.analytics }))}
                  className={`w-11 h-6 rounded-full flex items-center px-1 transition-all duration-300 flex-shrink-0 cursor-pointer ${
                    prefs.analytics ? 'bg-[#862ce0] justify-end' : 'bg-gray-600 justify-start'
                  }`}
                  aria-label="Toggle analytics cookies"
                >
                  <div className="w-4 h-4 rounded-full bg-white"></div>
                </button>
              </div>

              <div className="border-t border-white/10"></div>

              {/* Marketing */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-white text-sm font-semibold">Marketing Cookies</p>
                  <p className="text-gray-500 text-xs mt-0.5">Used to deliver relevant ads and track campaign performance.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setPrefs((p) => ({ ...p, marketing: !p.marketing }))}
                  className={`w-11 h-6 rounded-full flex items-center px-1 transition-all duration-300 flex-shrink-0 cursor-pointer ${
                    prefs.marketing ? 'bg-[#862ce0] justify-end' : 'bg-gray-600 justify-start'
                  }`}
                  aria-label="Toggle marketing cookies"
                >
                  <div className="w-4 h-4 rounded-full bg-white"></div>
                </button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={acceptAll}
              className="flex-1 sm:flex-none bg-gradient-to-r from-[#862ce0] to-[#c026d3] hover:from-[#7020c8] hover:to-[#a020b8] text-white font-bold text-sm px-6 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Accept All
            </button>
            <button
              type="button"
              onClick={acceptEssential}
              className="flex-1 sm:flex-none bg-gray-700 hover:bg-gray-600 text-white font-semibold text-sm px-6 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Essential Only
            </button>
            {expanded ? (
              <button
                type="button"
                onClick={saveCustom}
                className="flex-1 sm:flex-none border border-[#862ce0]/50 text-[#b47ef3] hover:bg-[#862ce0]/10 font-semibold text-sm px-6 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Save Preferences
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="flex-1 sm:flex-none border border-white/15 text-gray-400 hover:text-white hover:border-white/30 font-semibold text-sm px-6 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Manage Preferences
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
